import { FormRow } from 'framework/ui/layout';
import { useEffect, useState } from 'react';
import {
  DefaultInputPropTypes,
  RadioButtonGroup,
  TextInput,
  ClearAfterUnmountWrapper,
  FormFieldsData,
  useFormContext,
  useInputHelper,
  BOOLEAN_VALUE,
} from 'framework/form';
import { Box } from '@mui/material';
import { AccountNameInput } from 'domain/shared/account';
import AccountContactsInput from './AccountContactsInput';
import { GenerateCodeNameButton } from 'domain/shared/core/codename';

export const ACCOUNT_TYPE = {
  NEW: 'NEW',
  EXISTING: 'EXISTING',
};

const ACCOUNT_TYPES = [
  { value: ACCOUNT_TYPE.NEW, label: 'New' },
  { value: ACCOUNT_TYPE.EXISTING, label: 'Existing' },
];

const TYPE_SOURCE = 'accountType';

interface AccountInputProps extends DefaultInputPropTypes<any> {
  contactSource?: string;
  nameSource?: string;
  idSource?: string;
}

const AccountInput = ({ nameSource = '', idSource = '', contactSource = undefined, ...props }: AccountInputProps) => {
  const { getRawOnChangeCallback } = useInputHelper(props);
  const { initializeValue } = useInputHelper({
    source: TYPE_SOURCE,
  });

  const { data = {}, setFormData } = useFormContext();
  const type = data[TYPE_SOURCE] || ACCOUNT_TYPE.NEW;
  const onChangeCallback = getRawOnChangeCallback() || (() => {});
  const [isButtonDisplayed] = useState(!data.accountName);

  const changeAccountType = (value: { [x: string]: any }) => {
    const newType = value[TYPE_SOURCE];
    const params: any = {
      [TYPE_SOURCE]: newType,
      accountName: null,
    };

    if (newType === ACCOUNT_TYPE.EXISTING) {
      // on type switch previously entered data should be cleared
      params[nameSource] = '';
      params.hasAccountCodeName = false;
    } else {
      params[idSource] = null;

      if (contactSource) {
        params[contactSource] = null;
      }
    }

    if (onChangeCallback) {
      onChangeCallback(params);
    }
  };

  const onChangeAccount = (newValue: FormFieldsData) => {
    onChangeCallback({
      ...newValue,
      accountName: newValue.hasAccountCodeName ? newValue.accountName : '',
    });
  };

  useEffect(() => {
    let initialType = ACCOUNT_TYPE.NEW;
    if (data[idSource]) {
      initialType = ACCOUNT_TYPE.EXISTING;
    }

    initializeValue(initialType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setNewAccountName = (newName: any) => {
    if (setFormData) {
      setFormData({
        ...data,
        accountName: newName,
      });
    }
  };

  const isCodeNameFieldDisplayed =
    !data.hasAccountCodeName && !!data[idSource] && data?.isContractRenewal !== BOOLEAN_VALUE.YES;
  return (
    <>
      <FormRow>
        <RadioButtonGroup
          options={ACCOUNT_TYPES}
          onChangeCallback={changeAccountType}
          source={TYPE_SOURCE}
          label="Account type"
        />
      </FormRow>
      {type === ACCOUNT_TYPE.NEW && (
        <FormRow>
          <TextInput
            label="Original name"
            tooltip="The real-world name of the organization or a product"
            source="originalAccountName"
            errorSource="account.alias"
          />
          <TextInput
            label="Code name"
            tooltip="Internal Syberry name that will be used to refer to the project"
            source={nameSource}
            disabled
          />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <GenerateCodeNameButton
              source="accountName"
              isVisible={isButtonDisplayed}
              setCodeNameMethod={setNewAccountName}
            />
          </Box>
        </FormRow>
      )}
      {type === ACCOUNT_TYPE.EXISTING && (
        <>
          <FormRow>
            <AccountNameInput
              label="Account search"
              source={idSource}
              accountNameSource="accountCodeName"
              onChangeCallback={onChangeAccount}
              errorSource="account.alias"
            />
            <TextInput
              label="Code name"
              tooltip="Internal Syberry name that will be used to refer to the project"
              source={nameSource}
              disabled
              isVisible={isCodeNameFieldDisplayed}
              isRequired={isCodeNameFieldDisplayed}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <GenerateCodeNameButton
                source={nameSource}
                isVisible={isCodeNameFieldDisplayed}
                setCodeNameMethod={setNewAccountName}
              />
            </Box>
          </FormRow>
          <FormRow>
            {contactSource && data.accountId && (
              <ClearAfterUnmountWrapper>
                <AccountContactsInput
                  label="Primary contact"
                  placeholder="Choose primary contact"
                  source={contactSource}
                  accountId={data.accountId}
                />
              </ClearAfterUnmountWrapper>
            )}
          </FormRow>
        </>
      )}
    </>
  );
};

export default AccountInput;
